import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Briefcase, Users, SlidersHorizontal, LogOut, Layers, Menu, X, Palette } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/jobs", label: "Jobs", icon: Briefcase, roles: ["tenant_admin", "hr_member"] },
  { to: "/team", label: "Team", icon: Users, roles: ["tenant_admin", "hr_member"] },
  { to: "/settings", label: "Rating Metrics", icon: SlidersHorizontal, roles: ["tenant_admin", "hr_member"] },
  { to: "/branding", label: "Branding", icon: Palette, roles: ["tenant_admin", "hr_member"] },
];

const roleLabel = (r) => (r === "tenant_admin" ? "Admin" : r === "hr_member" ? "HR Member" : r);

function NavLinks({ user, onNavigate }) {
  const location = useLocation();
  return (
    <nav className="flex flex-col gap-1">
      {navItems.filter((n) => n.roles.includes(user?.role)).map((item) => {
        const active = location.pathname.startsWith(item.to);
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors duration-200 ${
              active ? "bg-[#007AFF]/10 text-[#007AFF]" : "text-[#1D1D1F] hover:bg-black/[0.03]"
            }`}
          >
            <Icon size={18} strokeWidth={1.8} />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

function Brand({ tenant }) {
  return (
    <div className="flex items-center gap-2.5 px-2">
      <div className="w-9 h-9 rounded-xl bg-[#007AFF] flex items-center justify-center">
        <Layers size={18} className="text-white" strokeWidth={2} />
      </div>
      <div>
        <div className="font-extrabold tracking-tight text-[15px] leading-none">HireScope</div>
        <div className="text-[11px] text-[#86868B] mt-0.5 truncate max-w-[150px]">{tenant?.name}</div>
      </div>
    </div>
  );
}

function UserBlock({ user, onSignOut }) {
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <>
      <div className="flex items-center gap-3 px-2 py-2">
        <div className="w-9 h-9 rounded-full bg-[#1D1D1F] text-white flex items-center justify-center text-xs font-bold">{initials}</div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{user?.name}</div>
          <div className="text-[11px] text-[#86868B]">{roleLabel(user?.role)}</div>
        </div>
      </div>
      <button onClick={onSignOut} data-testid="logout-btn"
        className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-[#FF3B30] hover:bg-[#FF3B30]/5 transition-colors">
        <LogOut size={18} strokeWidth={1.8} /> Sign out
      </button>
    </>
  );
}

export default function AppLayout({ children }) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const signOut = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 hidden md:flex flex-col bg-white border-r border-black/5 px-4 py-6">
        <div className="mb-8"><Brand tenant={tenant} /></div>
        <NavLinks user={user} />
        <div className="mt-auto pt-4 border-t border-black/5">
          <UserBlock user={user} onSignOut={signOut} />
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-black/5 h-14 flex items-center justify-between px-4">
        <Brand tenant={tenant} />
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu"
          className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"><Menu size={20} /></button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-72 bg-white p-4 flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="mobile-drawer">
            <div className="flex items-center justify-between mb-6">
              <Brand tenant={tenant} />
              <button onClick={() => setOpen(false)} className="text-[#86868B]"><X size={22} /></button>
            </div>
            <NavLinks user={user} onNavigate={() => setOpen(false)} />
            <div className="mt-auto pt-4 border-t border-black/5">
              <UserBlock user={user} onSignOut={signOut} />
            </div>
          </div>
        </div>
      )}

      <main className="md:pl-64">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">{children}</div>
      </main>
    </div>
  );
}
